import React, { useEffect, useState } from "react";
import { Plus, Pencil, Search, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const TYPE_LABEL = { supplier: "Supplier", konveksi: "Konveksi", other: "Lainnya" };
const PARTNER_TYPES = [
  { v: "supplier_kain", l: "Supplier Kain" },
  { v: "supplier_aksesori", l: "Supplier Aksesori" },
  { v: "cutting", l: "Cutting" },
  { v: "konveksi", l: "Konveksi" },
  { v: "barang_jadi", l: "Supplier Barang Jadi" },
  { v: "jasa_lain", l: "Jasa Lainnya" },
];
const ptLabel = (v) => (PARTNER_TYPES.find((p) => p.v === v) || {}).l || TYPE_LABEL[v] || v;

export default function Suppliers() {
  const { can } = useAuth();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const write = can("warehouse_admin", "purchasing");

  const load = () => {
    api.get("/suppliers", { params: { q: q || undefined, active: activeFilter === "all" ? undefined : activeFilter, partner_type: typeFilter === "all" ? undefined : typeFilter } })
      .then((r) => setList(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeFilter, typeFilter]);

  return (
    <div data-testid="suppliers-page">
      <PageHeader title="Master Mitra" subtitle="Supplier, konveksi, cutting & mitra produksi (satu mitra bisa multi-tipe)">
        {write && <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-supplier-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><Plus className="w-4 h-4 mr-1.5" /> Mitra</Button>}
      </PageHeader>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Cari nama / PIC / telepon..." className="pl-9 bg-white" data-testid="supplier-search" />
        </div>
        <Button variant="outline" onClick={load} className="border-taupe text-cocoa">Cari</Button>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-52 bg-white" data-testid="supplier-type-filter"><SelectValue placeholder="Jenis Mitra" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Jenis</SelectItem>
            {PARTNER_TYPES.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={activeFilter} onValueChange={setActiveFilter}>
          <SelectTrigger className="w-40 bg-white" data-testid="supplier-active-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua</SelectItem>
            <SelectItem value="active">Aktif</SelectItem>
            <SelectItem value="inactive">Nonaktif</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Panel>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Nama</TableHead><TableHead>Jenis</TableHead><TableHead>PIC</TableHead>
              <TableHead>Telepon</TableHead><TableHead className="text-center">Lead Time</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((s) => (
              <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                <TableCell className="font-semibold text-cocoa">{s.name}{s.code && <span className="block text-[11px] font-mono text-wmuted">{s.code}</span>}</TableCell>
                <TableCell>{(s.partner_types && s.partner_types.length) ? s.partner_types.map(ptLabel).join(", ") : (TYPE_LABEL[s.type] || s.type)}</TableCell>
                <TableCell>{s.pic || "-"}</TableCell>
                <TableCell>{s.phone || "-"}</TableCell>
                <TableCell className="text-center">{s.lead_time_days ? `${s.lead_time_days} hari` : "-"}</TableCell>
                <TableCell><span className={s.active ? "text-status-passed" : "text-wmuted"}>{s.active ? "Aktif" : "Nonaktif"}</span></TableCell>
                <TableCell className="text-right">
                  {write && <Button size="icon" variant="ghost" onClick={() => { setEdit(s); setOpen(true); }} data-testid={`edit-supplier-${s.id}`}><Pencil className="w-4 h-4 text-cocoa" /></Button>}
                </TableCell>
              </TableRow>
            ))}
            {!list.length && <TableRow><TableCell colSpan={7} className="text-center text-wmuted py-10"><Building2 className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada supplier.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Panel>

      <SupplierDialog open={open} setOpen={setOpen} edit={edit} onDone={load} />
    </div>
  );
}

function SupplierDialog({ open, setOpen, edit, onDone }) {
  const empty = { name: "", type: "supplier", partner_types: [], code: "", pic: "", phone: "", address: "", payment_term: "", notes: "", active: true, lead_time_days: 0 };
  const [form, setForm] = useState(empty);
  useEffect(() => { if (open) setForm(edit ? { ...empty, ...edit, partner_types: edit.partner_types || [] } : empty); /* eslint-disable-next-line */ }, [open, edit]);

  const togglePt = (v) => setForm((f) => ({ ...f, partner_types: f.partner_types.includes(v) ? f.partner_types.filter((x) => x !== v) : [...f.partner_types, v] }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama mitra wajib diisi");
    const payload = { ...form, lead_time_days: parseInt(form.lead_time_days) || 0 };
    try {
      if (edit) await api.put(`/suppliers/${edit.id}`, payload);
      else await api.post("/suppliers", payload);
      toast.success("Mitra disimpan"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">{edit ? "Edit" : "Tambah"} Mitra</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Nama *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" /></div>
          <div><Label>Kode</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Otomatis" data-testid="supplier-code-input" /></div>
          <div className="col-span-2">
            <Label>Tipe Mitra (bisa lebih dari satu)</Label>
            <div className="grid grid-cols-2 gap-1.5 mt-1" data-testid="supplier-partner-types">
              {PARTNER_TYPES.map((p) => (
                <label key={p.v} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.partner_types.includes(p.v)} onChange={() => togglePt(p.v)} data-testid={`pt-${p.v}`} className="accent-cocoa" />
                  {p.l}
                </label>
              ))}
            </div>
          </div>
          <div><Label>Termin Pembayaran</Label><Input value={form.payment_term} onChange={(e) => setForm({ ...form, payment_term: e.target.value })} placeholder="Cash / Tempo 30 hari" /></div>
          <div><Label>Lead Time (hari)</Label><Input type="number" value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })} data-testid="supplier-leadtime-input" /></div>
          <div><Label>PIC</Label><Input value={form.pic} onChange={(e) => setForm({ ...form, pic: e.target.value })} /></div>
          <div><Label>Telepon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="col-span-2"><Label>Alamat</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div className="col-span-2"><Label>Catatan</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
          <div className="col-span-2 flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="supplier-active-switch" /><Label>Aktif</Label></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} data-testid="save-supplier-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
